import { FunctionalComponent } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { GameEngine } from '@game/GameEngine';
import { initSuperJumpDebugPane } from '@app/debug/initSuperJumpDebugPane';

type ShellPhase = 'loading' | 'game';

type Vector2 = {
	x: number;
	y: number;
};

type GameOverReason = 'fell' | 'dead';

export const GameShell: FunctionalComponent = () => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const engine = useMemo(() => new GameEngine(), []);
	const [phase, setPhase] = useState<ShellPhase>('loading');
	const [progress, setProgress] = useState(0);
	const [isGameOver, setIsGameOver] = useState(false);
	const [gameOverReason, setGameOverReason] = useState<GameOverReason>('fell');
	const lastInputRef = useRef<Vector2>({ x: 0, y: 0 });

	useEffect(() => {
		let cancelled = false;
		engine
			.loadAssets((value) => {
				if (!cancelled) {
					setProgress(value);
				}
			})
			.then(() => {
				if (!cancelled) {
					setPhase('game');
				}
			});
		return () => {
			cancelled = true;
		};
	}, [engine]);

	useEffect(() => {
		if (phase !== 'game') return;
		const container = containerRef.current;
		if (!container) return;
		engine.start(container).catch((error) => console.error('Failed to start engine', error));
		engine.setOnGameOver((reason) => {
			setGameOverReason(reason);
			setIsGameOver(true);
		});
		const debugPane = initSuperJumpDebugPane({
			onChange: (patch) => engine.setTuning(patch),
			onEnemiesFrozenChange: (frozen) => engine.setEnemiesFrozen(frozen),
			onPlayerAnimationOverride: (state) => engine.setPlayerAnimationOverride(state),
			onRestartPlayerAnimationOverride: () => engine.restartPlayerAnimationOverride()
		});
		return () => {
			engine.setOnGameOver(null);
			debugPane.dispose();
			engine.stop().catch((error) => console.error('Failed to stop engine', error));
		};
	}, [engine, phase]);

	useEffect(() => {
		return () => {
			engine.stop().catch(() => undefined);
		};
	}, [engine]);

	useEffect(() => {
		if (phase !== 'game') return;
		const { x, y } = lastInputRef.current;
		engine.setInputDirection(x, y);
	}, [engine, phase]);

	const handleJoystickChange = useCallback(
		(vector: Vector2) => {
			if (isGameOver) return;
			lastInputRef.current = vector;
			engine.setInputDirection(vector.x, vector.y);
		},
		[engine, isGameOver]
	);

	const handleResetScene = () => {
		setIsGameOver(false);
		setGameOverReason('fell');
		engine.resetScene().catch((error) => console.error('Failed to reset scene', error));
		lastInputRef.current = { x: 0, y: 0 };
		engine.setInputDirection(0, 0);
	};

	const handleFire = () => {
		if (isGameOver) return;
		engine.triggerFire();
	};

	useEffect(() => {
		if (phase !== 'game') return;
		if (isGameOver) return;
		const container = containerRef.current;
		if (!container) return;

		type SwipeState = {
			pointerId: number;
			startX: number;
			startY: number;
			startT: number;
			pointerType: string;
		};
		let swipe: SwipeState | null = null;

		const isBlockedTarget = (target: EventTarget | null) => {
			const el = target instanceof Element ? target : null;
			if (!el) return false;
			// don't steal pointer events from HUD controls / debug pane
			return Boolean(el.closest('.joystick') || el.closest('.fire-fab') || el.closest('.tp-dfwv'));
		};

		const onDown = (e: PointerEvent) => {
			if (isBlockedTarget(e.target)) return;
			if (e.button !== 0) return;
			swipe = {
				pointerId: e.pointerId,
				startX: e.clientX,
				startY: e.clientY,
				startT: performance.now(),
				pointerType: e.pointerType ?? 'mouse'
			};
		};

		const onUp = (e: PointerEvent) => {
			if (!swipe || swipe.pointerId !== e.pointerId) return;
			const tuning = engine.getTuning();
			if (!tuning || !tuning.dashEnabled) {
				swipe = null;
				return;
			}
			const dt = (performance.now() - swipe.startT) / 1000;
			const dx = e.clientX - swipe.startX;
			const dy = e.clientY - swipe.startY;
			const dist = Math.hypot(dx, dy);
			// Desktop/mouse swipes are usually slower; allow a bit more time for pointerType=mouse.
			const maxTime = swipe.pointerType === 'mouse' ? Math.max(tuning.dashSwipeMaxTime, 0.35) : tuning.dashSwipeMaxTime;
			if (dt <= maxTime && dist >= tuning.dashSwipeMinDistancePx) {
				engine.triggerDash(dx / dist, dy / dist);
			}
			swipe = null;
		};

		const onCancel = (e: PointerEvent) => {
			if (!swipe || swipe.pointerId !== e.pointerId) return;
			swipe = null;
		};

		container.addEventListener('pointerdown', onDown, { passive: true });
		window.addEventListener('pointerup', onUp, { passive: true });
		window.addEventListener('pointercancel', onCancel, { passive: true });
		return () => {
			container.removeEventListener('pointerdown', onDown);
			window.removeEventListener('pointerup', onUp);
			window.removeEventListener('pointercancel', onCancel);
		};
	}, [engine, isGameOver, phase]);

	// Desktop fallback: press Space to dash in the current joystick direction.
	useEffect(() => {
		if (phase !== 'game') return;
		if (isGameOver) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.code !== 'Space') return;
			const tuning = engine.getTuning();
			if (!tuning || !tuning.dashEnabled) return;
			const v = lastInputRef.current;
			const len = Math.hypot(v.x, v.y);
			if (len < 0.2) return;
			e.preventDefault();
			engine.triggerDash(v.x / len, v.y / len);
		};
		window.addEventListener('keydown', onKeyDown, { passive: false });
		return () => window.removeEventListener('keydown', onKeyDown);
	}, [engine, isGameOver, phase]);

	return (
		<div className="shell">
			<div ref={containerRef} className={`shell__viewport ${phase === 'game' ? 'shell__viewport--active' : ''}`} />

			{phase === 'loading' && (
				<div className="overlay overlay--center">
					<p className="overlay__label">Подготовка шаблона</p>
					<p className="overlay__value">{progress}%</p>
				</div>
			)}

			{phase === 'game' && !isGameOver && (
				<div className="shell__hud">
					<Joystick onChange={handleJoystickChange} />
					<button type="button" className="fire-hit" onClick={handleFire} aria-label="Стрелять">
						<span className="fire-fab">Fire</span>
					</button>
				</div>
			)}

			{phase === 'game' && isGameOver && (
				<div className="overlay overlay--center">
					<p className="overlay__eyebrow">Game Over</p>
					<h2 className="overlay__title">{gameOverReason === 'dead' ? 'Жизни закончились' : 'Ты упал вниз'}</h2>
					<p className="overlay__body">
						{gameOverReason === 'dead'
							? 'Тебя добили. Нажми рестарт, чтобы начать заново.'
							: 'Нажми рестарт, чтобы начать заново.'}
					</p>
					<div className="overlay__actions">
						<button type="button" className="button" onClick={handleResetScene}>
							Restart
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

type JoystickProps = {
	onChange: (vector: Vector2) => void;
};

const Joystick: FunctionalComponent<JoystickProps> = ({ onChange }) => {
	const baseRef = useRef<HTMLDivElement | null>(null);
	const [knobPosition, setKnobPosition] = useState<Vector2>({ x: 0, y: 0 });
	const activePointerId = useRef<number | null>(null);
	const activePointerType = useRef<string | null>(null);

	const reset = useCallback(() => {
		setKnobPosition({ x: 0, y: 0 });
		onChange({ x: 0, y: 0 });
	}, [onChange]);

	useEffect(() => {
		return () => reset();
	}, [reset]);

	const updateFromPointer = (clientX: number, clientY: number) => {
		const base = baseRef.current;
		if (!base) return;
		const rect = base.getBoundingClientRect();
		const centerX = rect.left + rect.width / 2;
		const centerY = rect.top + rect.height / 2;
		const deltaX = clientX - centerX;
		const deltaY = clientY - centerY;
		const radius = rect.width / 2;
		const distance = Math.min(Math.hypot(deltaX, deltaY), radius);
		const angle = Math.atan2(deltaY, deltaX);
		const x = Math.cos(angle) * distance;
		const y = Math.sin(angle) * distance;
		setKnobPosition({ x, y });
		onChange({ x: x / radius, y: y / radius });
	};

	const handlePointerDown = (event: PointerEvent) => {
		event.preventDefault();
		activePointerId.current = event.pointerId;
		activePointerType.current = event.pointerType;
		baseRef.current?.setPointerCapture?.(event.pointerId);
		updateFromPointer(event.clientX, event.clientY);
	};

	const handlePointerMove = (event: PointerEvent) => {
		if (activePointerId.current !== event.pointerId) return;
		event.preventDefault();
		updateFromPointer(event.clientX, event.clientY);
	};

	const handlePointerEnd = (event: PointerEvent) => {
		if (activePointerId.current !== event.pointerId) return;
		baseRef.current?.releasePointerCapture?.(event.pointerId);
		activePointerId.current = null;
		activePointerType.current = null;
		reset();
	};

	useEffect(() => {
		const onWindowPointerUp = (event: PointerEvent) => {
			// Some browsers/devices may not reliably dispatch pointerup to the captured element.
			if (activePointerId.current !== event.pointerId) return;
			handlePointerEnd(event);
		};
		const onWindowBlur = () => {
			if (activePointerId.current == null) return;
			activePointerId.current = null;
			activePointerType.current = null;
			reset();
		};
		window.addEventListener('pointerup', onWindowPointerUp, { passive: false });
		window.addEventListener('pointercancel', onWindowPointerUp, { passive: false });
		window.addEventListener('blur', onWindowBlur);
		return () => {
			window.removeEventListener('pointerup', onWindowPointerUp);
			window.removeEventListener('pointercancel', onWindowPointerUp);
			window.removeEventListener('blur', onWindowBlur);
		};
	}, [reset]);

	return (
		<div
			ref={baseRef}
			className="joystick"
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerEnd}
			onPointerCancel={handlePointerEnd}
		>
			<div className="joystick__ring" />
			<div
				className="joystick__knob"
				style={{
					left: `calc(50% + ${knobPosition.x}px)`,
					top: `calc(50% + ${knobPosition.y}px)`
				}}
			/>
		</div>
	);
};
